import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Checkbox, Group, Stack, Switch, Textarea, TextInput } from '@mantine/core';
import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';

import { PasteAddressSection } from './PasteAddressSection';
import { shipToStepMessages } from './ship-to-step-messages';
import styles from './ShipToStep.module.css';
import { AddressForm } from '../../../../address/address-form';
import { VALIDATED_ADDRESS_STATUS, VERIFICATION_STATUS_CODE } from '../../../../address/address-validation.api';
import { useAddressValidation } from '../../../../hooks/use-address-validation';
import { LTL_SUPPORTED_COUNTRY_CODES } from '../../../../ltl/constants';
import { areCustomerLtlDefaultsDirty } from '../../../../ltl/ltl-defaults';
import { AddressValidation } from '../../../wizard-drawer/flows/create-label/sections/ship-to/address-validation/address-validation';
import { addressValidationMessages } from '../../../wizard-drawer/flows/create-label/sections/ship-to/address-validation/address-validation-messages';
import { REQUIRED_SHIP_TO_ADDRESS_FIELDS, sliceFieldErrors } from '../../wizard-step-validation';
import { DELIVERY_SERVICE_FIELDS } from '../services-grid/delivery-service-fields';
import { ServicesGrid } from '../services-grid/ServicesGrid';

import type { AddressFormReferenceData } from '../../../../address/address-form';
import type { AddressValidationResultItem, MatchedAddress, ValidatedAddressStatus } from '../../../../address/address-validation.api';
import type { Customer, CustomerLtlDefaults } from '../../../../types/customer';
import type { LtlAddress } from '../../../../types/ltl';
import type { State } from '../../../../types/reference-data';
import type { FreightBookingFormState, WizardDeliveryServices } from '../../freight-booking-wizard-types';
import type { MessageDescriptor } from 'react-intl';

const DELIVERY_FIELD_BY_KEY: Record<keyof WizardDeliveryServices, MessageDescriptor> = DELIVERY_SERVICE_FIELDS.reduce(
  (accumulator, field) => ({ ...accumulator, [field.key]: field.label }),
  {} as Record<keyof WizardDeliveryServices, MessageDescriptor>,
);

// country is US-only so the picker is hidden. Note: the country field has id 'country',
// not 'countryCode' — matching schema-generator filterHiddenFields which uses field.id.
const HIDDEN_ADDRESS_FIELDS = ['country'] as const;

// Static reference data — the wizard is domestic US only so countries and
// loading flags are not needed.
const buildReferenceData = (states: State[]): AddressFormReferenceData => ({
  countries: [],
  states,
  isLoadingCountries: false,
  isLoadingStates: false,
  sellerHomeCountry: 'US',
});

export type ShipToStepProps = {
  formState: FreightBookingFormState;
  onFormChange: (next: FreightBookingFormState) => void;
  onCustomerSelect?: (customer: Customer) => void;
  onPasteAddressUsed?: () => void;
  states: State[];
  /** Formatted inline error messages keyed by field path (e.g. `'destinationAddress.city'`). */
  fieldErrors?: Record<string, string>;
  isAddressValidationEnabled?: boolean;
  /** Called after a validation or apply completes so the container can persist the result to the backend. */
  onAddressValidated?: (address: LtlAddress) => void;
  /** True while the selected customer's LTL Defaults are loading. Disables delivery service edits. */
  isLoadingCustomerLtlDefaults?: boolean;
  /** True while the save-back API call is in flight. Disables the save-defaults checkbox. */
  isSavingLtlDeliveryDefaults?: boolean;
  /** Permission gate — hide the save-defaults checkbox when the user lacks the Configuration role. */
  canSaveLtlDeliveryDefaults?: boolean;
  /**
   * True when the selected customer already has saved LTL defaults.
   * Drives the "Save as default" vs "Update default" label switch.
   */
  hasSavedLtlDeliveryDefaults?: boolean;
  /** The currently selected customer's saved LTL Defaults (or null), used to detect unsaved changes. */
  loadedCustomerLtlDefaults?: CustomerLtlDefaults | null;
};

const ADDRESS_PREFIX = 'destinationAddress';

const VALIDATION_REQUIRED_FIELDS: readonly (keyof LtlAddress)[] = ['line1', 'city', 'state', 'postalCode'];

const STORED_STATUS_TO_VALIDATED: Record<string, ValidatedAddressStatus | undefined> = {
  [VERIFICATION_STATUS_CODE.VERIFIED]: VALIDATED_ADDRESS_STATUS.VERIFIED,
  [VERIFICATION_STATUS_CODE.WARNING]: VALIDATED_ADDRESS_STATUS.WARNING,
  [VERIFICATION_STATUS_CODE.ERROR]: VALIDATED_ADDRESS_STATUS.ERROR,
};

const VALIDATED_STATUS_TO_STORED: Record<string, string> = {
  [VALIDATED_ADDRESS_STATUS.VERIFIED]: VERIFICATION_STATUS_CODE.VERIFIED,
  [VALIDATED_ADDRESS_STATUS.WARNING]: VERIFICATION_STATUS_CODE.WARNING,
  [VALIDATED_ADDRESS_STATUS.ERROR]: VERIFICATION_STATUS_CODE.ERROR,
};

const buildInitialValidationResult = (
  verificationStatus: string | null | undefined,
  verificationMessage: string | null | undefined,
): AddressValidationResultItem | null => {
  const status = verificationStatus ? STORED_STATUS_TO_VALIDATED[verificationStatus] : undefined;
  if (!status) return null;
  return {
    status,
    matchedAddress: null,
    originalAddress: null,
    messages: verificationMessage ? [verificationMessage] : [],
    validationMessages: [],
  };
};

export const ShipToStep = ({
  formState,
  onFormChange,
  onCustomerSelect,
  onPasteAddressUsed,
  states,
  fieldErrors,
  isAddressValidationEnabled = false,
  onAddressValidated,
  isLoadingCustomerLtlDefaults,
  isSavingLtlDeliveryDefaults,
  canSaveLtlDeliveryDefaults,
  hasSavedLtlDeliveryDefaults,
  loadedCustomerLtlDefaults,
}: ShipToStepProps) => {
  const intl = useIntl();

  const destination = formState.destinationAddress;

  const initialValidationResult = useRef(buildInitialValidationResult(destination.verificationStatus, destination.verificationMessage));
  const { validationResult, isValidating, validate, clearValidation } = useAddressValidation(initialValidationResult.current);

  const handleAddressChange = (next: Partial<LtlAddress>) => {
    onFormChange({
      ...formState,
      destinationAddress: { ...destination, ...next, verificationStatus: null, verificationMessage: null, verificationUtc: null },
    });
    clearValidation();
  };

  const handleTradeshowField = (field: keyof FreightBookingFormState['tradeshow'], value: string) =>
    onFormChange({ ...formState, tradeshow: { ...formState.tradeshow, [field]: value } });

  const referenceData = useMemo(() => buildReferenceData(states), [states]);

  const isAddressComplete = VALIDATION_REQUIRED_FIELDS.every((field) => !!destination[field]);

  const handleValidate = async () => {
    const result = await validate({
      company: destination.company ?? '',
      line1: destination.line1 ?? '',
      line2: destination.line2 ?? '',
      line3: '',
      city: destination.city ?? '',
      state: destination.state ?? '',
      postalCode: destination.postalCode ?? '',
      countryCode: destination.countryCode ?? 'US',
    });
    const isNetworkError = result.status === VALIDATED_ADDRESS_STATUS.ERROR;
    const verificationMessage = isNetworkError
      ? intl.formatMessage(addressValidationMessages.validationNetworkError)
      : (result.messages[0] ?? null);
    const validatedAddress: LtlAddress = {
      ...destination,
      verificationStatus: VALIDATED_STATUS_TO_STORED[result.status] ?? null,
      verificationMessage,
      verificationUtc: DateTime.utc().toISO(),
    };
    onFormChange({ ...formState, destinationAddress: validatedAddress });
    onAddressValidated?.(validatedAddress);
  };

  const handleApply = (matched: MatchedAddress) => {
    const appliedAddress: LtlAddress = {
      ...destination,
      line1: matched.line1,
      line2: matched.line2,
      city: matched.city,
      state: matched.state,
      postalCode: matched.postalCode,
      verificationStatus: VERIFICATION_STATUS_CODE.VERIFIED,
      verificationMessage: null,
      verificationUtc: DateTime.utc().toISO(),
    };
    onFormChange({ ...formState, destinationAddress: appliedAddress });
    onAddressValidated?.(appliedAddress);
    clearValidation();
  };

  const isDirty = areCustomerLtlDefaultsDirty(formState, loadedCustomerLtlDefaults);
  const showSaveCheckbox = (!!formState.selectedCustomerId || formState.shouldSaveAsNewCustomer) && canSaveLtlDeliveryDefaults;

  // Every write funnels through here so the save-back intent can never outlive the
  // conditions that permitted it — same reasoning as the Ship From step's handleFormChange.
  const handleFormChange = (next: FreightBookingFormState) => {
    const isStillDirty = areCustomerLtlDefaultsDirty(next, loadedCustomerLtlDefaults);
    onFormChange(next.shouldSaveLtlDeliveryDefaults && !isStillDirty ? { ...next, shouldSaveLtlDeliveryDefaults: false } : next);
  };

  const handleDeliveryServicesChange = (next: WizardDeliveryServices) => handleFormChange({ ...formState, deliveryServices: next });

  // Track whether the user has explicitly opened the instructions textarea.
  // Resets on customer change so switching to a customer with no saved instructions
  // hides the textarea again. The OR with !!formState.deliveryInstructions ensures
  // the textarea is shown immediately when a customer change prefills instructions.
  const [instructionsOpenedByUser, setInstructionsOpenedByUser] = useState(!!formState.deliveryInstructions);
  // Holds the text while the switch is off so the toggle is non-destructive.
  const hiddenInstructionsRef = useRef('');
  useEffect(() => {
    setInstructionsOpenedByUser(!!formState.deliveryInstructions);
    hiddenInstructionsRef.current = '';
    // Only re-sync when the selected customer changes, not on every formState mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.selectedCustomerId]);
  const showDeliveryInstructions = instructionsOpenedByUser || !!formState.deliveryInstructions;

  return (
    <Stack gap='md'>
      <PasteAddressSection onParsedAddress={handleAddressChange} onPasteAddressUsed={onPasteAddressUsed} />
      <AddressForm
        addressType='shipTo'
        countryCode='US'
        customerCountryCodes={LTL_SUPPORTED_COUNTRY_CODES}
        errors={fieldErrors ? sliceFieldErrors(fieldErrors, ADDRESS_PREFIX) : undefined}
        hiddenFields={HIDDEN_ADDRESS_FIELDS}
        onChange={(next) => handleAddressChange(next as Partial<LtlAddress>)}
        onCustomerSelect={onCustomerSelect}
        referenceData={referenceData}
        requiredFields={REQUIRED_SHIP_TO_ADDRESS_FIELDS}
        showAddressLine3={false}
        value={destination}
      />

      {isAddressValidationEnabled && (
        <AddressValidation
          isAddressComplete={isAddressComplete}
          isValidating={isValidating}
          onApply={handleApply}
          onValidate={handleValidate}
          result={validationResult}
        />
      )}

      <Checkbox
        checked={formState.shouldSaveAsNewCustomer}
        label={intl.formatMessage(shipToStepMessages.saveAsNewCustomer)}
        onChange={(event) => {
          const shouldSaveAsNewCustomer = event.currentTarget.checked;
          const shouldKeepSaveDefaults = shouldSaveAsNewCustomer || !!formState.selectedCustomerId;
          onFormChange({
            ...formState,
            shouldSaveAsNewCustomer,
            shouldSaveLtlDeliveryDefaults: shouldKeepSaveDefaults && formState.shouldSaveLtlDeliveryDefaults,
          });
        }}
      />

      <span className={styles.servicesHeader}>
        <FormattedMessage {...shipToStepMessages.deliveryServicesHeader} />
      </span>
      <ServicesGrid
        disabled={isLoadingCustomerLtlDefaults}
        fields={DELIVERY_SERVICE_FIELDS}
        onChange={handleDeliveryServicesChange}
        value={formState.deliveryServices}
      />

      {formState.deliveryServices.isTradeshowDelivery && (
        <Group grow>
          <TextInput
            data-testid='tradeshow-name'
            label={intl.formatMessage(shipToStepMessages.tradeshowNameLabel)}
            maxLength={100}
            onChange={(event) => handleTradeshowField('name', event.currentTarget.value)}
            value={formState.tradeshow.name}
          />
          <TextInput
            data-testid='tradeshow-booth'
            label={intl.formatMessage(shipToStepMessages.boothNumberLabel)}
            maxLength={100}
            onChange={(event) => handleTradeshowField('boothNumber', event.currentTarget.value)}
            value={formState.tradeshow.boothNumber}
          />
        </Group>
      )}

      <Switch
        checked={showDeliveryInstructions}
        data-testid='delivery-instructions-toggle'
        label={intl.formatMessage(shipToStepMessages.deliveryInstructionsLabel)}
        onChange={(event) => {
          const isOn = event.currentTarget.checked;
          setInstructionsOpenedByUser(isOn);
          if (isOn) {
            handleFormChange({ ...formState, deliveryInstructions: hiddenInstructionsRef.current });
          } else {
            hiddenInstructionsRef.current = formState.deliveryInstructions;
            handleFormChange({ ...formState, deliveryInstructions: '' });
          }
        }}
      />
      {showDeliveryInstructions && (
        <Textarea
          data-testid='delivery-instructions'
          label={intl.formatMessage(shipToStepMessages.deliveryInstructionsLabel)}
          onChange={(event) => handleFormChange({ ...formState, deliveryInstructions: event.currentTarget.value })}
          value={formState.deliveryInstructions}
        />
      )}

      {showSaveCheckbox && (
        <Checkbox
          checked={formState.shouldSaveLtlDeliveryDefaults}
          classNames={{ description: styles.saveDefaultsDescription }}
          data-testid='save-ltl-delivery-defaults'
          description={intl.formatMessage(shipToStepMessages.ltlDeliveryDefaultsHelperText)}
          disabled={!isDirty || isSavingLtlDeliveryDefaults}
          label={
            hasSavedLtlDeliveryDefaults
              ? intl.formatMessage(shipToStepMessages.updateDefaultSelections)
              : intl.formatMessage(shipToStepMessages.saveAsDefaultSelections)
          }
          onChange={(event) => handleFormChange({ ...formState, shouldSaveLtlDeliveryDefaults: event.currentTarget.checked })}
        />
      )}
    </Stack>
  );
};

export type ShipToStepSummaryProps = {
  formState: FreightBookingFormState;
};

export const ShipToStepSummary = ({ formState }: ShipToStepSummaryProps) => {
  const intl = useIntl();

  const destination = formState.destinationAddress;
  const cityStateZip = [destination.city, destination.state, destination.postalCode].filter(Boolean).join(', ');

  const activeServices = DELIVERY_SERVICE_FIELDS.filter((field) => formState.deliveryServices[field.key]).map((field) =>
    intl.formatMessage(DELIVERY_FIELD_BY_KEY[field.key]),
  );

  const hasTradeshow = formState.deliveryServices.isTradeshowDelivery && (formState.tradeshow.name || formState.tradeshow.boothNumber);

  const tradeshowLine = (() => {
    const { name, boothNumber } = formState.tradeshow;
    if (name && boothNumber) return intl.formatMessage(shipToStepMessages.tradeshowLine, { name, booth: boothNumber });
    if (name) return intl.formatMessage(shipToStepMessages.tradeshowNameOnly, { name });
    if (boothNumber) return intl.formatMessage(shipToStepMessages.tradeshowBoothOnly, { booth: boothNumber });
    return null;
  })();

  return (
    <div className={styles.summary}>
      <div className={styles.summarySection}>
        <span className={styles.sectionLabel}>
          <FormattedMessage {...shipToStepMessages.destinationSectionLabel} />
        </span>
        {destination.name && <span className={styles.summaryPrimary}>{destination.name}</span>}
        {destination.company && <span className={styles.summaryPrimary}>{destination.company}</span>}
        {destination.line1 && <span className={styles.summarySecondary}>{destination.line1}</span>}
        {cityStateZip && <span className={styles.summarySecondary}>{cityStateZip}</span>}
        {destination.phone && <span className={styles.summarySecondary}>{destination.phone}</span>}
      </div>

      {activeServices.length > 0 && (
        <div className={styles.summarySection}>
          <span className={styles.sectionLabel}>
            <FormattedMessage {...shipToStepMessages.deliveryServicesHeader} />
          </span>
          <div className={styles.tags}>
            {activeServices.map((service) => (
              <Badge color='light' key={service} size='lg' variant='filled'>
                {service}
              </Badge>
            ))}
          </div>
          {hasTradeshow && tradeshowLine && <span className={styles.summarySecondary}>{tradeshowLine}</span>}
        </div>
      )}
    </div>
  );
};
